import { useEffect, useState } from 'react';
import { Loader2Icon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import type { Question, QuestionCategory, CreateQuestionInput } from '@kennisbank/shared';
import { ApiError } from '../../api/client.js';
import { createQuestion, updateQuestion, deleteQuestion } from './AdminElearningApi.js';
import { clearDraft, readDraft, writeDraft } from './draftStorage.js';

interface LocalOption {
  text: string;
  isCorrect: boolean;
}

interface QuestionDraft {
  title: string;
  questionText: string;
  explanation: string;
  points: number;
  category: QuestionCategory;
  isRequired: boolean;
  isLive: boolean;
  options: LocalOption[];
  videoMoment?: string;
}

/** "12:34", "12:34.5" of "1:02:03" → seconden; leeg → null; ongeldig → undefined. */
export function parseVideoMoment(v: string): number | null | undefined {
  if (!v.trim()) return null;
  if (!/^\d+(:\d{1,2}){0,2}(\.\d)?$/.test(v.trim())) return undefined;
  return v
    .trim()
    .split(':')
    .reduce((acc, part) => acc * 60 + Number(part), 0);
}

function formatVideoMoment(sec: number | null | undefined): string {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const rest = Math.round((sec % 60) * 10) / 10;
  return `${m}:${rest < 10 ? '0' : ''}${rest}`;
}

/**
 * Vraag-editor modal. Geport van MoveLogic `QuestionEditorModal`.
 * Meerkeuze met één juist antwoord (radio); categorie oefen/examen; punten;
 * uitleg (oefenmodus); verplicht-vlag (bepaalt de examRequiredCount-pool);
 * actief-vlag. Antwoordopties krijgen bij opslaan hun sortOrder = index.
 */
export function AdminQuestionModal({
  courseId,
  moduleId,
  question,
  onClose,
  onSaved,
}: {
  courseId: string;
  moduleId: string;
  question: Question | null; // null = nieuw
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const draftKey = `question.${courseId}.${moduleId}.${question?.id ?? 'new'}`;
  const [draft] = useState(() => readDraft<QuestionDraft>(draftKey));
  const [restored] = useState(draft !== null);
  const [title, setTitle] = useState(draft?.title ?? question?.title ?? '');
  const [questionText, setQuestionText] = useState(
    draft?.questionText ?? question?.questionText ?? '',
  );
  const [explanation, setExplanation] = useState(draft?.explanation ?? question?.explanation ?? '');
  const [points, setPoints] = useState(draft?.points ?? question?.points ?? 1);
  const [category, setCategory] = useState<QuestionCategory>(
    draft?.category ?? question?.category ?? 'oefen',
  );
  const [isRequired, setIsRequired] = useState(draft?.isRequired ?? question?.isRequired ?? false);
  const [isLive, setIsLive] = useState(draft?.isLive ?? question?.isLive ?? true);
  const [videoMoment, setVideoMoment] = useState(
    draft?.videoMoment ?? formatVideoMoment(question?.videoSeconds),
  );
  const videoSeconds = category === 'oefen' ? parseVideoMoment(videoMoment) : null;
  const [options, setOptions] = useState<LocalOption[]>(
    draft?.options && draft.options.length >= 2
      ? draft.options
      : question && question.answerOptions.length > 0
        ? [...question.answerOptions]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((o) => ({ text: o.text, isCorrect: o.isCorrect }))
        : [
            { text: '', isCorrect: true },
            { text: '', isCorrect: false },
          ],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autosave als concept bij elke wijziging — een misklik of gesloten tab
  // kost zo nooit werk; het concept komt terug bij heropenen van de modal.
  useEffect(() => {
    writeDraft<QuestionDraft>(draftKey, {
      title,
      questionText,
      explanation,
      points,
      category,
      isRequired,
      isLive,
      options,
      videoMoment,
    });
  }, [
    draftKey,
    title,
    questionText,
    explanation,
    points,
    category,
    isRequired,
    isLive,
    options,
    videoMoment,
  ]);

  function handleCancel(): void {
    clearDraft(draftKey);
    onClose();
  }

  function addOption(): void {
    if (options.length >= 10) return;
    setOptions([...options, { text: '', isCorrect: false }]);
  }

  function removeOption(index: number): void {
    if (options.length <= 2) return;
    const next = options.filter((_, i) => i !== index);
    if (!next.some((o) => o.isCorrect) && next.length > 0) next[0].isCorrect = true;
    setOptions(next);
  }

  function setOptionText(index: number, text: string): void {
    setOptions(options.map((o, i) => (i === index ? { ...o, text } : o)));
  }

  function setCorrect(index: number): void {
    setOptions(options.map((o, i) => ({ ...o, isCorrect: i === index })));
  }

  const hasValidOptions =
    options.length >= 2 && options.every((o) => o.text.trim()) && options.some((o) => o.isCorrect);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    const body: CreateQuestionInput = {
      title,
      questionText: questionText || undefined,
      explanation: explanation || undefined,
      points,
      category,
      isRequired,
      isLive,
      videoSeconds,
      answerOptions: options.map((o, i) => ({
        text: o.text,
        isCorrect: o.isCorrect,
        sortOrder: i,
      })),
    };
    try {
      if (question) {
        await updateQuestion(courseId, moduleId, question.id, body);
      } else {
        await createQuestion(courseId, moduleId, body);
      }
      clearDraft(draftKey);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!question) return;
    if (!window.confirm('Weet je zeker dat je deze vraag wilt verwijderen?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteQuestion(courseId, moduleId, question.id);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt.');
      setSaving(false);
    }
  }

  // Bewust géén onClick-close op de backdrop: een misklik naast de modal
  // mag ingevoerd werk niet weggooien. Sluiten kan alleen expliciet.
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-brand-100 px-6 py-4">
          <h2 className="text-lg font-bold text-ink-900">
            {question ? 'Vraag bewerken' : 'Nieuwe vraag'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-700/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="thin-scroll space-y-5 overflow-y-auto px-6 py-5">
          {restored && (
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-ink-700">
              Je eerder niet-opgeslagen concept is hersteld. Kies Annuleren om het weg te gooien.
            </p>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Titel
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Korte titel voor de vraag"
              className="w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Vraagtekst
            </label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              rows={3}
              placeholder="De volledige vraag die aan de cursist wordt gesteld…"
              className="w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Antwoordopties
            </label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="correct-answer"
                    checked={option.isCorrect}
                    onChange={() => setCorrect(index)}
                    title="Markeer als juist antwoord"
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                  />
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => setOptionText(index, e.target.value)}
                    placeholder={`Antwoord ${index + 1}`}
                    className="flex-1 rounded-lg border border-ring2-border bg-white px-3 py-1.5 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      aria-label="Optie verwijderen"
                      className="rounded-md p-1.5 text-ink-700/60 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2Icon size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 10 && (
              <button
                type="button"
                onClick={addOption}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-white px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <PlusIcon size={14} /> Optie toevoegen
              </button>
            )}
            <p className="mt-2 text-xs text-ink-700/60">
              Selecteer het juiste antwoord met het ronde keuzevakje.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
              Uitleg (voor oefenmodus)
            </label>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              rows={2}
              placeholder="Uitleg die na het beantwoorden in de oefenmodus wordt getoond…"
              className="w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
                Punten
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={points}
                onChange={(e) => setPoints(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
                Categorie
              </label>
              <div className="flex gap-4 pt-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="category"
                    checked={category === 'oefen'}
                    onChange={() => setCategory('oefen')}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-ink-900">Oefenvraag</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="category"
                    checked={category === 'examen'}
                    onChange={() => setCategory('examen')}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-ink-900">Examenvraag</span>
                </label>
              </div>
            </div>
          </div>

          {category === 'oefen' && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ink-900/60">
                Moment in video (optioneel)
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="mm:ss"
                value={videoMoment}
                onChange={(e) => setVideoMoment(e.target.value)}
                className="w-32 rounded-lg border border-ring2-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
              />
              <p className="mt-1 text-xs text-ink-900/50">
                {videoSeconds === undefined
                  ? 'Gebruik mm:ss, bijvoorbeeld 8:34 of 8:34.5.'
                  : 'Ingevuld: de lesvideo van dit moduul pauzeert hier en toont deze vraag.'}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                className="h-4 w-4 rounded border-ink-100 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-ink-900">
                Verplichte examenvraag
                <span className="ml-1 font-normal text-ink-700/60">
                  (telt mee in het verplichte deel van het examen)
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={isLive}
                onChange={(e) => setIsLive(e.target.checked)}
                className="h-4 w-4 rounded border-ink-100 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-ink-900">
                Actief
                <span className="ml-1 font-normal text-ink-700/60">
                  (inactieve vragen worden niet getoond)
                </span>
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex items-center justify-between border-t border-brand-100 px-6 py-4">
          <div>
            {question && (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                <Trash2Icon size={14} /> Verwijderen
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded-full px-4 py-1.5 text-sm text-ink-700 hover:bg-brand-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !title.trim() || !hasValidOptions || videoSeconds === undefined}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {saving && <Loader2Icon size={14} className="animate-spin" />}
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
